import { useEffect } from "react";

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  noindex?: boolean;
}

export const SEO: React.FC<SEOProps> = ({
  title = "OnlyOneTap - Find Trusted Local Service Providers for Home Services",
  description = "OnlyOneTap connects you with reliable local service providers for cleaning, plumbing, electrician services, and more household needs. Book trusted professionals in just one tap.",
  keywords = "home services, local service providers, cleaning services, plumbing services, electrician, household services, home repair, OnlyOneTap, service booking",
  image = "/OnlyOneTapLogo.jpeg",
  url = "https://www.onlyonetap.com/",
  type = "website",
  noindex = false,
}) => {
  const fullTitle = title.includes("OnlyOneTap")
    ? title
    : `${title} | OnlyOneTap`;
  const fullUrl = url.startsWith("http")
    ? url
    : `https://www.onlyonetap.com${url}`;
  const fullImage = image.startsWith("http")
    ? image
    : `https://www.onlyonetap.com${image}`;

  useEffect(() => {
    // Update title
    document.title = fullTitle;

    // Helper function to update or create meta tags
    const updateMetaTag = (selector: string, content: string) => {
      let element = document.querySelector(selector);
      if (element) {
        element.setAttribute("content", content);
      } else {
        element = document.createElement("meta");
        const parts = selector.match(/\[(.+?)=["'](.+?)["']\]/);
        if (parts) {
          element.setAttribute(parts[1], parts[2]);
          element.setAttribute("content", content);
          document.head.appendChild(element);
        }
      }
    };

    // Helper function to update or create link tags
    const updateLinkTag = (rel: string, href: string) => {
      let element = document.querySelector(`link[rel="${rel}"]`);
      if (element) {
        element.setAttribute("href", href);
      } else {
        element = document.createElement("link");
        element.setAttribute("rel", rel);
        element.setAttribute("href", href);
        document.head.appendChild(element);
      }
    };

    // Update all meta tags
    updateMetaTag('meta[name="title"]', fullTitle);
    updateMetaTag('meta[name="description"]', description);
    updateMetaTag('meta[name="keywords"]', keywords);
    updateMetaTag(
      'meta[name="robots"]',
      noindex ? "noindex, nofollow" : "index, follow"
    );

    // Open Graph tags
    updateMetaTag('meta[property="og:type"]', type);
    updateMetaTag('meta[property="og:url"]', fullUrl);
    updateMetaTag('meta[property="og:title"]', fullTitle);
    updateMetaTag('meta[property="og:description"]', description);
    updateMetaTag('meta[property="og:image"]', fullImage);
    updateMetaTag('meta[property="og:site_name"]', "OnlyOneTap");

    // Twitter tags
    updateMetaTag('meta[name="twitter:card"]', "summary_large_image");
    updateMetaTag('meta[name="twitter:url"]', fullUrl);
    updateMetaTag('meta[name="twitter:title"]', fullTitle);
    updateMetaTag('meta[name="twitter:description"]', description);
    updateMetaTag('meta[name="twitter:image"]', fullImage);

    // Update canonical URL
    updateLinkTag("canonical", fullUrl);
  }, [fullTitle, description, keywords, fullUrl, fullImage, type, noindex]);

  return null;
};
